/**
 * Output Sanitizer
 * 
 * Sanitizes AI outputs to remove system prompt leakage before display.
 * This prevents internal instructions, context variables, and raw JSON
 * from appearing in the UI.
 */

// Patterns that indicate prompt leakage
const FORBIDDEN_PATTERNS = [
  /if coach-uploaded documents exist/gi,
  /act as a (?:business )?coach/gi,
  /context:\s*\{/gi,
  /system:\s*/gi,
  /assistant:\s*/gi,
  /instructions:\s*/gi,
  /<think>/gi,
  /<\/think>/gi,
  /\{[^}]*business_stage[^}]*\}/gi,
  /\{[^}]*growth_stage[^}]*\}/gi,
  /\{[^}]*workload_intensity[^}]*\}/gi,
  /business_stage:\s*\w+/gi,
  /growth_stage:\s*\w+/gi,
  /YOU ARE A COACHING FRAMEWORK/gi,
  /=== CRITICAL RULES ===/gi,
  /=== HOW TO RESPOND ===/gi,
  /FRAMEWORK PROGRESS/gi,
  /\[CURRENT\]/gi,
  /\[DONE\]/gi,
  /\[NEXT\]/gi,
  /=== WHAT YOU'VE ALREADY LEARNED/gi,
  /Stage \d+:.*\[(?:✅|👉|⏳)/gi,
];

/**
 * Sanitize text by removing forbidden patterns
 * @param {string} text - Text to sanitize
 * @returns {string} - Cleaned text
 */
export const sanitizeAIOutput = (text) => {
  if (!text) return "";
  
  let cleaned = text;
  
  // Remove forbidden patterns
  for (const pattern of FORBIDDEN_PATTERNS) {
    cleaned = cleaned.replace(pattern, '');
  }
  
  // Remove JSON-like structures with context variables
  cleaned = cleaned.replace(/\{[^}]*(?:_stage|_intensity|context)[^}]*\}/g, '');
  
  // Remove multiple consecutive newlines
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n');
  
  // Remove leading/trailing whitespace
  cleaned = cleaned.trim();
  
  return cleaned;
};

/**
 * Check if text contains prompt leakage
 * @param {string} text - Text to check
 * @returns {boolean} - True if leakage detected
 */
export const hasPromptLeakage = (text) => {
  if (!text) return false;
  
  for (const pattern of FORBIDDEN_PATTERNS) {
    if (pattern.test(text)) {
      return true;
    }
  }
  
  return false;
};

/**
 * Validate and sanitize text for display
 * @param {string} text - Text to validate
 * @returns {{isValid: boolean, cleaned: string}} - Validation result
 */
export const validateForDisplay = (text) => {
  if (hasPromptLeakage(text)) {
    const cleaned = sanitizeAIOutput(text);
    // If still has leakage after cleaning, reject
    if (hasPromptLeakage(cleaned)) {
      return { isValid: false, cleaned: "" };
    }
    return { isValid: true, cleaned };
  }
  return { isValid: true, cleaned: text };
};

/**
 * Sanitize a tool result before rendering
 * @param {Object} toolData - Tool data object
 * @returns {Object} - Sanitized tool data
 */
export const sanitizeToolData = (toolData) => {
  if (!toolData) return toolData;
  
  const sanitized = { ...toolData };
  
  // Sanitize string fields
  if (sanitized.title) {
    sanitized.title = sanitizeAIOutput(sanitized.title);
  }
  if (sanitized.content) {
    sanitized.content = sanitizeAIOutput(sanitized.content);
  }
  if (sanitized.description) {
    sanitized.description = sanitizeAIOutput(sanitized.description);
  }
  
  // Sanitize arrays
  if (Array.isArray(sanitized.insights)) {
    sanitized.insights = sanitized.insights.map(i => sanitizeAIOutput(i));
  }
  if (Array.isArray(sanitized.items)) {
    sanitized.items = sanitized.items.map(item => {
      if (typeof item === 'string') {
        return sanitizeAIOutput(item);
      }
      if (typeof item === 'object' && item !== null) {
        return {
          ...item,
          label: item.label ? sanitizeAIOutput(item.label) : item.label,
          value: item.value ? sanitizeAIOutput(String(item.value)) : item.value
        };
      }
      return item;
    });
  }
  
  return sanitized;
};

export default {
  sanitizeAIOutput,
  hasPromptLeakage,
  validateForDisplay,
  sanitizeToolData
};

